import React, { useState, useEffect, useRef, useCallback } from "react";
import { Table, Checkbox, message, Space } from "antd";
import { SettingOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { fetchSavedColumnsAsync, saveColumnsAsync } from "../Slices/columnSettingsSlice";
import { AntdButton } from "./InputField";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { HolderOutlined } from "@ant-design/icons";
import ReactDOM from "react-dom";

const type = "DraggableColumn";

export const CustomGridTable = ({
    columns,
    dataSource,
    pagination,
    onChange,
    locale,
    size,
    scroll,
    onRow,
    footer,
    expandable,
    rowKey,
    style,
    rowSelection,
    Id,
    summary,
    loading,
    enableColumnResize = true,
    enableColumnToggle = true,
    disableColumnDrag = false,
}) => {

    const dispatch = useDispatch();
    const savedVisible = useSelector(
        (state) => state.columnSettings.saved[Id] || []
    );

    const loginUser = useSelector(
        (state) => state?.loginUserInfo?.loginUserDetails
    );

    const [columnWidths, setColumnWidths] = useState({});
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState([]);

    const [columnOrder, setColumnOrder] = useState([]);

    const panelRef = useRef(null);



    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popoverOpen && panelRef.current && !panelRef.current.contains(event.target)) {
                setPopoverOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [popoverOpen]);

    useEffect(() => {
        const allKeys = columns.map((col) => col.key || col.dataIndex);

        setVisibleColumns((prev) =>
            prev.length ? prev : savedVisible.length ? savedVisible : allKeys
        );

        setColumnOrder((prev) =>
            prev.length ? prev : allKeys
        );
    }, [columns, savedVisible]);



    useEffect(() => {
        if (loginUser?.id && Id) {
            dispatch(fetchSavedColumnsAsync({ employeeId: loginUser.id, tableId: Id }));
        }
    }, [loginUser?.id, Id, dispatch]);


    const handleSaveColumns = () => {
        dispatch(
            saveColumnsAsync({
                employeeId: loginUser.id,
                tableId: Id,
                visibleColumns,
            })
        )
            .then(() => message.success("Columns saved successfully!"))
            .catch(() => message.error("Failed to save column settings."));
        setPopoverOpen(false);
    };

    const handleMoveColumn = useCallback((dragKey, hoverKey) => {
        setColumnOrder((prev) => {
            const newOrder = [...prev];
            const dragIndex = newOrder.indexOf(dragKey);
            const hoverIndex = newOrder.indexOf(hoverKey);
            if (dragIndex === -1 || hoverIndex === -1) return prev;

            const [removed] = newOrder.splice(dragIndex, 1);
            newOrder.splice(hoverIndex, 0, removed);
            return newOrder;
        });
    }, []);


    const filteredColumns = columnOrder
        .filter((key) => visibleColumns.includes(key))
        .map((key) => {
            const col = columns.find(
                (c) => (c.key || c.dataIndex) === key
            );
            if (!col) return null;

            const width = columnWidths[key] || col.width;

            return {
                ...col,
                width,
                ellipsis: true,
            };
        })
        .filter(Boolean);

    const handleColumnToggle = (columnKey) => {
        setVisibleColumns((prev) => {
            if (prev.includes(columnKey)) {
                if (prev.length === 1) return prev;
                return prev.filter((key) => key !== columnKey);
            }
            return [...prev, columnKey];
        });
    };

    const columnsWithHeaderMeta = filteredColumns.map((col) => {
        const key = col.key || col.dataIndex;

        return {
            ...col,
            onHeaderCell: () => ({
                width: col.width,
                column: col,
                handleMoveColumn,
                onResize:
                    enableColumnResize &&
                    ((e, { size }) =>
                        setColumnWidths((prev) => ({
                            ...prev,
                            [key]: size.width,
                        }))),
            }),
        };
    });

    const finalColumns = enableColumnToggle
        ? [
            ...columnsWithHeaderMeta,
            {

                title: (
                    <div style={{ position: 'relative' }}>
                        <SettingOutlined
                            style={{ cursor: 'pointer', fontSize: 16 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setPopoverOpen((prev) => !prev);
                            }}
                        />

                        {popoverOpen &&
                            ReactDOM.createPortal(
                                <div
                                    ref={panelRef}
                                    style={{
                                        position: 'absolute', top: '50px', right: 10, width: 220, background: '#fff', border: '1px solid #ddd', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                        zIndex: 10000,
                                        padding: 8,
                                    }}
                                    onClick={(e) => e.stopPropagation()} // stop closing when clicked inside
                                >
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        {columns.map((col) => {
                                            const key = col.key || col.dataIndex;
                                            return (
                                                <Checkbox
                                                    key={key}
                                                    checked={visibleColumns.includes(key)}
                                                    onChange={() => handleColumnToggle(key)}
                                                >
                                                    {col.title}
                                                </Checkbox>
                                            );
                                        })}

                                        <AntdButton
                                            label="Save"
                                            type="primary"
                                            size="small"
                                            onClick={handleSaveColumns}
                                        />
                                    </Space>
                                </div>,
                                document.body
                            )}
                    </div>
                ),
                key: 'settings-column',
                width: 50,
                fixed: 'right',
                render: () => null,
            }

        ]
        : columnsWithHeaderMeta;

    return (
        <DndProvider backend={HTML5Backend}>
            {/* Default compact styles applied to all CustomGridTable instances */}
            <style>{`
    /* Apply compact mode to ALL CustomGridTable instances */
    .compact-grid-table,
    .compact-grid-table .ant-table,
    .compact-grid-table .ant-table-tbody > tr > td,
    .compact-grid-table .ant-table-thead > tr > th {
        font-size: 12px !important;
        line-height: 1.2 !important;
        padding: 4px 6px !important; /* Tighter row height */
    }
 
    /* Make header compact */
    .compact-grid-table .ant-table-thead > tr > th {
        padding: 6px 8px !important;
        font-weight: 600;
        background: #fafafa;
    }
 
    /* Compact buttons inside table */
    .compact-grid-table .ant-btn {
        padding: 2px 6px !important;
        height: auto !important;
        font-size: 12px !important;
    }
 
    /* Compact checkbox */
    .compact-grid-table .ant-checkbox-wrapper {
        font-size: 12px !important;
    }
 
    /* Compact table row height */
    .compact-grid-table .ant-table-row {
        height: 28px !important;
    }

    .compact-grid-table th .drag-icon {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.compact-grid-table th:hover .drag-icon {
  opacity: 1;
}

 /* Remove pointer from entire sortable header */
.ant-table-column-has-sorters {
  cursor: default !important;
}

/* Disable click on whole sorter wrapper */
.ant-table-column-has-sorters .ant-table-column-sorters {
  pointer-events: none !important;
  cursor: default !important;
}

/* Enable click ONLY on actual sort arrows */
.ant-table-column-has-sorters .ant-table-column-sorter {
  pointer-events: auto !important;
  cursor: pointer !important;
}

 

.drag-icon {
  cursor: url("/black-move-cursor.cur"), move  !important;
}
  
/* === FORCE SAME BACKGROUND FOR ALL HEADER CELLS === */
.compact-grid-table .ant-table-thead > tr > th,
.compact-grid-table .ant-table-thead > tr > th.ant-table-cell-fix-right,
.compact-grid-table .ant-table-thead > tr > th.ant-table-cell-fix-left {
    background: #fafafa !important;
}


/* === FORCE SAME BACKGROUND FOR ALL BODY CELLS === */
.compact-grid-table .ant-table-tbody > tr > td,
.compact-grid-table .ant-table-tbody > tr > td.ant-table-cell-fix-right,
.compact-grid-table .ant-table-tbody > tr > td.ant-table-cell-fix-left {
    background: #ffffff !important;
}


/* === REMOVE STRIPED ROW EFFECT (if using table-striped) === */
.compact-grid-table .ant-table-tbody > tr:nth-child(odd) > td {
    background: #ffffff !important;
}


/* === FORCE SAME HOVER COLOR FOR ALL COLUMNS === */
.compact-grid-table .ant-table-tbody > tr:hover > td,
.compact-grid-table .ant-table-tbody > tr:hover > td.ant-table-cell-fix-right,
.compact-grid-table .ant-table-tbody > tr:hover > td.ant-table-cell-fix-left {
    background: #f5f5f5 !important;
}



 
`}</style>

            <div className="card-body" style={{ paddingTop: 0 }}>
                <Table
                    className="table-striped compact-grid-table"
                    rowKey={rowKey}
                    columns={finalColumns}
                    dataSource={dataSource}
                    pagination={pagination}
                    onChange={onChange}
                    locale={locale}
                    size={size}
                    onRow={onRow}
                    scroll={scroll}
                    bordered
                    footer={footer}
                    expandable={expandable}
                    rowSelection={rowSelection}
                    summary={summary}
                    loading={loading}
                    style={style}
                    components={{
                        header: {
                            cell: (props) =>
                                disableColumnDrag ? (
                                    <th {...props}>{props.children}</th>   // ⭐ NO DRAG
                                ) : enableColumnResize ? (
                                    <ResizableDraggableTitle
                                        {...props}
                                        handleMoveColumn={handleMoveColumn}
                                    />
                                ) : (
                                    <DraggableTitle
                                        {...props}
                                        handleMoveColumn={handleMoveColumn}
                                    />
                                ),
                        },
                    }}

                />
            </div>
        </DndProvider>
    );
};


const DraggableTitle = (props) => {
    const { column, handleMoveColumn, children, ...restProps } = props;

    const dropRef = useRef(null);
    const dragRef = useRef(null);

    const colKey = column?.key || column?.dataIndex;
    const isDragDisabled = column?.disableDrag;

    // 🔥 Drag only from icon
    const [{ isDragging }, drag] = useDrag({
        type,
        item: { key: colKey },
        canDrag: !isDragDisabled,
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    // 🔥 Drop on whole header
    const [, drop] = useDrop({
        accept: type,
        hover(item, monitor) {
            if (!dropRef.current) return;

            if (item.key === colKey) return;

            // 🔥 Only move when mouse is actually over header
            handleMoveColumn(item.key, colKey);

            // 🔥 Update dragged key to prevent infinite calls
            item.key = colKey;
        },
    });

    drag(dragRef);
    drop(dropRef);

    return (
        <th
            {...restProps}
            ref={dropRef}
            style={{
                ...restProps.style,
                opacity: isDragging ? 0.5 : 1,
                position: "relative",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                }}
            >
                {/* ✅ DRAG ICON ONLY */}
                {!isDragDisabled && (
                    <HolderOutlined
                        ref={dragRef}
                        className="drag-icon"
                        style={{
                            fontSize: 14,
                            color: "#999",
                            cursor: "move",   // 🔥 4-direction cursor
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    />



                )}

                {/* ✅ SORTER WORKS HERE */}
                <span style={{ flex: 1 }}>
                    {children}
                </span>
            </div>
        </th>
    );
};


const ResizableDraggableTitle = (props) => {
    const { onResize, width, column, handleMoveColumn, ...restProps } = props;

    const dropRef = useRef(null);
    const dragRef = useRef(null);

    const colKey = column?.key || column?.dataIndex;
    const isDragDisabled = column?.disableDrag;

    const [, drag] = useDrag({
        type,
        item: { key: colKey },
        canDrag: !isDragDisabled,
    });

    const [, drop] = useDrop({
        accept: type,
        hover(item) {
            if (!isDragDisabled && item.key !== colKey) {
                handleMoveColumn(item.key, colKey);
            }
        },
    });

    drag(dragRef);   // ✅ drag only from icon
    drop(dropRef);   // ✅ drop on entire header

    return (
        <th
            ref={dropRef}
            {...restProps}
            style={{
                position: "relative",
                ...restProps.style,
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                }}
            >
                {!isDragDisabled && (
                    <HolderOutlined
                        ref={dragRef}
                        className="drag-icon"
                        style={{
                            fontSize: 14,
                            color: "#999",
                            cursor: "move",   // 🔥 4-direction cursor
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    />

                )}

                <span>{restProps.children}</span>
            </div>

            {/* Resize Handle */}
            {width && onResize && (
                <div
                    style={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: 6,
                        cursor: "col-resize",
                        zIndex: 10,
                        borderRight: "2px solid transparent",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderRight = "2px solid #1677ff"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderRight = "2px solid transparent"; }}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        const startX = e.pageX;
                        const startWidth = width;

                        const onMove = (ev) => {
                            const newWidth = startWidth + ev.pageX - startX;
                            if (newWidth > 50) {
                                onResize(ev, { size: { width: newWidth } });
                            }
                        };

                        const stop = () => {
                            document.removeEventListener("mousemove", onMove);
                            document.removeEventListener("mouseup", stop);
                        };

                        document.addEventListener("mousemove", onMove);
                        document.addEventListener("mouseup", stop);
                    }}
                />
            )}
        </th>
    );
};


